import MenuIcon from "@mui/icons-material/Menu"
import SettingsIcon from "@mui/icons-material/Settings"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { ReportSettingsDialog } from "components/ReportSettings/ReportSettingsDialog"
import { ReportVersionDialog } from "components/ReportVersionDialog/ReportVersionDialog"
import { Person, Project, Report } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { getBusinessCustomerName } from "pages/Dashboard/ProjectViewsUtils"
import { JSX, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getTextForReportStatus } from "utils/status"

type ReportLeftBoxHeaderProps = {
  project: Project
  report: Report
  onNavigation: (nav: string) => void
  hasUnsavedChanges?: boolean
  onReportUpdate?: (updatedReport: Partial<Report>) => void
}

export const ReportLeftBoxHeader = (props: ReportLeftBoxHeaderProps): JSX.Element => {
  const { project, report, hasUnsavedChanges, onReportUpdate } = props
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [versionDialogOpen, setVersionDialogOpen] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)

  const [newReportName, setNewReportName] = useState("")
  const { leads, customers, employees } = useStoreState((state) => state)
  const navigate = useNavigate()
  // Store actions
  const { fetchEmployees, fetchLeads, fetchCustomers } = useStoreActions((actions) => actions)

  // Fetch commonly-used data as needed
  useEffect(() => {
    const ensureReferenceData = async () => {
      try {
        if (leads.length === 0) {
          await fetchLeads({ fresh: false })
        }
      } catch (err) {
        console.error("ReportLeftBoxHeader: failed to fetch leads", err)
      }

      try {
        if (customers.length === 0) {
          await fetchCustomers({ fresh: false })
        }
      } catch (err) {
        console.error("ReportLeftBoxHeader: failed to fetch customers", err)
      }
    }

    ensureReferenceData()
  }, [leads.length, customers.length, fetchLeads, fetchCustomers])

  // Lazily fetch employees only when the settings dialog opens
  useEffect(() => {
    const ensureEmployees = async () => {
      if (!settingsDialogOpen || employees.length > 0) {
        return
      }

      try {
        await fetchEmployees({ fresh: false })
      } catch (err) {
        console.error("ReportLeftBoxHeader: failed to fetch employees", err)
      }
    }

    ensureEmployees()
  }, [settingsDialogOpen, employees.length, fetchEmployees])

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorElUser(null)
  }

  // const handleOpenVersionDialog = () => {
  //   setVersionDialogOpen(true)
  //   handleCloseMenu()
  // }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h5">{project.name}</Typography>
        <Typography variant="caption">
          {getBusinessCustomerName(project.lead.opportunityID || "", leads, customers)}
        </Typography>
        <Typography variant="caption">Auftrag: {project.lead.opportunityID}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption">
            {report.documentNo} / {getTextForReportStatus(report.status)} / Ver. {report.version}
          </Typography>
          {hasUnsavedChanges && (
            <Chip
              label="Ungespeichert"
              size="small"
              color="warning"
              sx={{ height: 16, fontSize: "0.7rem" }}
            />
          )}
        </Box>
      </Box>

      <Box>
        <IconButton size="large" onClick={handleOpenMenu}>
          <MenuIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseMenu}
      >
        {/* <MenuItem onClick={handleOpenVersionDialog}>
          <HistoryIcon sx={{ mr: 1 }} fontSize="small" />
          <Typography sx={{ textAlign: "center" }}>Versionsverwaltung</Typography>
        </MenuItem> */}
        <MenuItem
          onClick={() => {
            setSettingsDialogOpen(true)
            handleCloseMenu()
          }}
        >
          <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
          <Typography sx={{ textAlign: "center" }}>Berichtseinstellungen</Typography>
        </MenuItem>
      </Menu>

      <Dialog open={copyDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Report kopieren</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name des neuen Reports"
            fullWidth
            variant="outlined"
            value={newReportName}
            onChange={(e) => setNewReportName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      <ReportVersionDialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        reportId={report.id}
        currentVersion={report.versionNumber || 1}
        onVersionCreated={(versionId) => {
          // Optionally reload the page or navigate to the new version
          navigate(`/projectportal/projects/${project.id}/report/${versionId}`)
        }}
        onVersionRestored={(versionId) => {
          // Navigate to the restored version
          navigate(`/projectportal/projects/${project.id}/report/${versionId}`)
        }}
      />

      {onReportUpdate && (
        <>
          <ReportSettingsDialog
            open={settingsDialogOpen}
            onClose={() => setSettingsDialogOpen(false)}
            report={report}
            onSave={(updatedReport) => {
              onReportUpdate(updatedReport)
              setSettingsDialogOpen(false)
            }}
            employees={
              employees?.map((e) => {
                const firstName = e.ContactInfo?.FirstName || ""
                const lastName = e.ContactInfo?.LastName || ""
                const email = e.ContactInfo?.Email || ""
                const phone = e.ContactInfo?.Phone1 || ""
                const jobTitle = e.ContactInfo?.Title || ""

                // Fallback: if ContactInfo missing, derive conservatively from EmployeeName
                let fn = firstName
                let ln = lastName
                if (!fn && !ln && e.EmployeeName) {
                  // Remove trailing ", Dr." or similar qualifiers and split by spaces
                  const cleaned = e.EmployeeName.replace(/,\s*[^,]+$/, "").trim()
                  const parts = cleaned.split(/\s+/)
                  if (parts.length > 1) {
                    // Assume first token is first name, rest last name
                    fn = parts[0]
                    ln = parts.slice(1).join(" ")
                  } else if (parts.length === 1) {
                    ln = parts[0]
                  }
                }

                return {
                  id: e.id,
                  email: email,
                  firstName: fn,
                  lastName: ln,
                  avatarURL: "",
                  jobTitle: jobTitle,
                  phone: phone,
                  phoneNumber: phone,
                } as Person
              }) || []
            }
          />
        </>
      )}
    </Box>
  )
}
